import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import ModalShell, { inputClass, textareaClass, labelClass, errorBoxClass } from '../ModalShell';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error';
  const { data, status } = error.response;
  if (status === 422 && data.detail) {
    if (Array.isArray(data.detail)) { const e = data.detail[0]; return typeof e === 'object' ? `${e.loc?.[e.loc.length-1]||'Field'}: ${e.msg||'Invalid'}` : String(e); }
    if (typeof data.detail === 'string') return data.detail;
  }
  return data.detail || data.message || `Request failed (${status})`;
};

const ReceiveForm = ({ purchaseOrder, onClose, onSuccess }) => {
  const [items, setItems] = useState([]);
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (purchaseOrder?.items) {
      setItems(purchaseOrder.items.map(i => ({
        purchase_order_item_id: i.id,
        product_id: i.product_id,
        product_name: i.product_name,
        product_sku: i.product_sku,
        quantity_ordered: String(i.quantity_ordered),
        quantity_received_so_far: String(i.quantity_received),
        remaining: String(i.remaining_quantity),
        quantity_to_receive: String(i.remaining_quantity),
        unit_cost: i.unit_cost,
      })));
    }
  }, [purchaseOrder]);

  const updateQty = (idx, val) => {
    const updated = [...items];
    updated[idx].quantity_to_receive = val;
    setItems(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const receiveItems = items.filter(i => Number(i.quantity_to_receive) > 0);
    if (receiveItems.length === 0) { setError('Enter at least one quantity to receive'); return; }
    for (const item of receiveItems) {
      if (Number(item.quantity_to_receive) > Number(item.remaining)) {
        setError(`Cannot receive more than remaining (${item.remaining}) for ${item.product_name}`);
        return;
      }
    }
    setLoading(true);
    try {
      await api.post('/purchase-receipts', {
        purchase_order_id: purchaseOrder.id,
        receipt_date: receiptDate,
        notes,
        items: receiveItems.map(i => ({
          purchase_order_item_id: i.purchase_order_item_id,
          product_id: i.product_id,
          quantity_received: i.quantity_to_receive,
        })),
      });
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell
      title={`Receive Items - ${purchaseOrder?.po_number}`}
      onClose={onClose}
      maxWidth="max-w-3xl"
      dataTestId="receive-form-modal"
      footer={
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-[#d1d5db] rounded-lg text-[#1a1a2e] hover:bg-white font-medium transition-colors" data-testid="cancel-receive-form">Cancel</button>
          <button type="submit" form="receive-form" disabled={loading} className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg font-medium transition-colors disabled:opacity-50" data-testid="create-receipt-button">{loading ? 'Creating...' : 'Create Receipt (Draft)'}</button>
        </div>
      }
    >
      <form id="receive-form" onSubmit={handleSubmit}>
        {error && <div className={errorBoxClass} data-testid="receive-form-error">{error}</div>}

        <div className="mb-3 p-3 bg-white rounded-lg border border-[#d1d5db] text-sm">
          <span className="font-medium">Supplier: </span>{purchaseOrder?.supplier?.name}
        </div>

        <div className="mb-4">
          <label className={labelClass}>Receipt Date</label>
          <input type="date" className={`${inputClass} max-w-xs`} value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} data-testid="receipt-date-input" />
        </div>

        <div className="bg-white border border-[#d1d5db] rounded-lg overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead className="bg-[#f9fafb] text-[#6b7280]">
              <tr>
                <th className="py-2 px-3 text-left">Product</th>
                <th className="py-2 px-3 text-center">Ordered</th>
                <th className="py-2 px-3 text-center">Already Received</th>
                <th className="py-2 px-3 text-center">Remaining</th>
                <th className="py-2 px-3 text-center">Receive Now</th>
                <th className="py-2 px-3 text-right">Unit Cost</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className="border-t border-[#e5e7eb]">
                  <td className="py-2 px-3 font-medium">{item.product_name} <span className="text-[#6b7280]">({item.product_sku})</span></td>
                  <td className="py-2 px-3 text-center">{item.quantity_ordered}</td>
                  <td className="py-2 px-3 text-center">{item.quantity_received_so_far}</td>
                  <td className="py-2 px-3 text-center font-medium">{item.remaining}</td>
                  <td className="py-2 px-3 text-center">
                    {Number(item.remaining) > 0 ? (
                      <input type="number" min="0.01" max={item.remaining} step="0.01" className="w-20 bg-white border border-[#d1d5db] rounded-lg px-2 py-1 text-center text-sm focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0] outline-none" value={item.quantity_to_receive} onChange={(e) => updateQty(idx, e.target.value)} data-testid={`receive-qty-${idx}`} />
                    ) : (
                      <span className="text-[#065F46] font-medium">Done</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right">Rs. {parseFloat(item.unit_cost).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <label className={labelClass}>Notes</label>
          <textarea className={textareaClass} rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="receive-notes" />
        </div>
      </form>
    </ModalShell>
  );
};

export default ReceiveForm;
